import firebase from "firebase";
import "firebase/firestore";
import "firebase/auth";
import "./services/firebase/firebase";
import { createTheme, MuiThemeProvider } from "@material-ui/core";
import { defaultTheme } from "./themes/default";
import { store } from "./state/configureStore";
import {
  signIn,
  setConnectingUser,
  setConnectingSettings,
  signOut,
  setUserSettings,
  updateUserData,
} from "./state/currentuser/currentUserReducer";
import FirebaseService from "./services/firebase/FirebaseService";
import { Provider } from "react-redux";
import ReadleeProfile from "./services/firebase/ReadleeProfile";
import StudentHomeScreenService from "./components/Screens/StudentHomeScreen/state/StudentHomeScreenService";
import { clearBooks } from "./components/Screens/StudentHomeScreen/state/StudentHomeScreenState";
import InternalApp from "./InternalApp";
import * as FS from "@fullstory/browser";
import TeacherStateService from "./state/teacherData/TeacherStateService";
import "./App.css";

const theme = createTheme(defaultTheme);

type WatchFunctionVariable = (() => void) | undefined;
let userWatchFunction: WatchFunctionVariable;
let settingsWatchFunction: WatchFunctionVariable;
let watchReadleeSettings: WatchFunctionVariable;

// Startup Services
const studentStateService = new StudentHomeScreenService();
const teacherStateService = new TeacherStateService();

let currentUser: ReadleeProfile | null = null;
let currentUserId = "";
let currentUserEmail = "";
let currentUserType = "";

// The current user assumed type allows for teachers to behave as students
let currentAssumedUserType: "" | "teacher" | "student" = "";

const userDataReceived = (userData: ReadleeProfile) => {
  currentUserId = userData?.uid;
  currentUserEmail = userData?.email;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  currentUserType = userData?.userType;

  // Just forcing the current user type to be typed to make things later
  if (currentUserType === "student" || currentUserType === "teacher") {
    currentAssumedUserType = currentUserType;
  }

  FS.setUserVars({
    userType_str: userData.userType,
    displayName: userData.displayName || `${userData.firstName} ${userData.lastName}`,
  });

  if (userData?.uid !== currentUser?.uid) {
    currentUser = userData;

    if (userData) {
      // Start the student watch
      // Teachers can sometimes be students, so teachers get student watches as well
      studentStateService.startWatch(userData.uid, userData.wordsPerMin);

      // Start teacher watches, if we're a teacher
      if (currentAssumedUserType === "teacher") {
        teacherStateService.startWatch(userData.uid);
      }
    }

    signIn(userData);
  } else {
    updateUserData(userData);
  }
};

firebase.auth().onAuthStateChanged(user => {
  if (user === null) {
    // clear user if we signed out
    studentStateService.endWatch();
    teacherStateService.endWatch();
    signOut();
    clearBooks();

    currentUser = null;

    if (userWatchFunction !== undefined) {
      userWatchFunction();
      userWatchFunction = undefined;
    }

    if (settingsWatchFunction !== undefined) {
      settingsWatchFunction();
      settingsWatchFunction = undefined;
    }

    if (watchReadleeSettings !== undefined) {
      watchReadleeSettings();
      watchReadleeSettings = undefined;
    }
  } else {
    // is connecting = true
    setConnectingUser(true);
    setConnectingSettings(true);

    FirebaseService.setLastLoginDay(user.uid);
    FS.identify(user.uid, {
      displayName: user.displayName || "",
      email: user.email || "",
    });

    settingsWatchFunction = FirebaseService.watchUserSettings(user.uid, setUserSettings);
    watchReadleeSettings = FirebaseService.watchReadleeSettings();
    userWatchFunction = FirebaseService.watchUser(user.uid, userDataReceived);
  }
});

function App() {
  return (
    <MuiThemeProvider theme={theme}>
      <Provider store={store}>
        <InternalApp />
      </Provider>
    </MuiThemeProvider>
  );
}

export default App;

export { currentUserId, currentUserEmail, currentAssumedUserType };
